import { useEffect, useRef, type ReactNode } from 'react';

import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/shared/ui/shadcn/resizable';
import { cn } from '@/lib/utils';

import { overlayDockPortalSystem } from './OverlayDockPortalContext';

interface OverlayDockHostProps {
  /** Content to render inside the dock */
  children?: ReactNode;
  /** Additional CSS classes for the dock container */
  className?: string;
  /** ID for panel layout persistence */
  persistenceId?: string;
}

/**
 * OverlayDockHost — provides a portal target and renders a bottom dock surface.
 *
 * - Absolutely positioned to cover the parent (expects parent to be `relative`)
 * - Bottom panel is resizable (react-resizable-panels)
 * - Uses a spacer panel above to create the "peek" area
 */
export function OverlayDockHost({
  children,
  className,
  persistenceId = 'overlay-dock-panel',
}: OverlayDockHostProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const containerElementRef = useRef<HTMLDivElement | null>(null);
  const PortalHost = overlayDockPortalSystem.PortalHost;

  useEffect(() => {
    const containerElement = containerElementRef.current;
    if (!containerElement) {
      return;
    }
    const hostParent = hostRef.current?.parentElement;
    if (!hostParent) {
      return;
    }

    const update = () => {
      const height = containerElement.getBoundingClientRect().height;
      hostParent.style.setProperty('--overlay-dock-height', `${height}px`);
      hostParent.style.setProperty('--overlay-dock-inset', `calc(${height}px + 12px)`);
    };

    update();
    const observer = new ResizeObserver(() => update());
    observer.observe(containerElement);

    return () => observer.disconnect();
  }, []);

  return (
    <PortalHost
      className={className}
      render={({ containerRef, children: hostChildren, className: hostClassName }) => (
        <div
          ref={hostRef}
          className={cn('absolute inset-0 z-40 pointer-events-none', hostClassName)}
          role="region"
        >
          <ResizablePanelGroup id={persistenceId} orientation="vertical" className="h-full">
            <ResizablePanel
              id="spacer"
              defaultSize="70%"
              minSize="30%"
              className="pointer-events-none"
            />
            <ResizableHandle
              withHandle
              className="pointer-events-auto !h-px !w-full [&>div]:rotate-90"
            />
            <ResizablePanel
              id="dock"
              defaultSize="30%"
              minSize="15%"
              className={cn(
                'pointer-events-auto flex flex-col shadow-lg',
                'bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/80'
              )}
            >
              <div
                ref={(node) => {
                  containerElementRef.current = node;
                  containerRef(node);
                }}
                className="flex-1 overflow-hidden"
              >
                {hostChildren}
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      )}
    >
      {children}
    </PortalHost>
  );
}
